import React, { useMemo, useRef, useState } from 'react';
import { IStrongBoxOpoFilter, IStrongBoxOpoOrderListing } from '@nkr/models/strong-box';
import { Utility } from '@nkr/services/utility';
import { Alert, Col, Row } from 'react-bootstrap';
import { LoadingIndicator } from '@nkr/components/loading-indicator';
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, RowClassParams, ValueGetterParams } from 'ag-grid-community';
import { GridWrapper } from '../strong-box/styles/styles'
import { AgGridReact } from 'ag-grid-react';
import { TLegendEntry } from '../strong-box/legend';
import { useAppHookContext } from '@nkr/utils/rest-api';
import StrongBoxFilter from '../strong-box/strong-box-filter';
import useStrongBoxOpoOrders from '@nkr/store/strong-box-opo/hooks/use-strong-box-opo-orders';
import SortableHeaderComponent from '../recipients/recipient-shield/components/sortable-header';
import DonorCenterCell from '../strong-box/donor-center-cell';

const StrongBoxOpoOrders = () => {
    const { app: { user } } = useAppHookContext();

    const defaultFilter: IStrongBoxOpoFilter = {
        startDate: Utility.getFirstOfCurrentMonth(),
        // startDate: moment('2026-01-01').toDate(),
        endDate: Utility.getLastOfCurrentMonth(),
        isCenter: false,
        opoId: user.opoId == 0 ? null : user.opoId
    }

    const [filter, setFilter] = useState<IStrongBoxOpoFilter>(defaultFilter);
    const { list, loading, error } = useStrongBoxOpoOrders(filter);

    const colDefs: ColDef[] = [
        {
            field: 'opoName',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>OPO</SortableHeaderComponent>
        },
        {
            colId: 'createdDate',
            field: 'createdDate',
            headerName: 'Ordered Date',
            width: 120,
            comparator: Utility.dateComparator,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Ordered Date</SortableHeaderComponent>,
        },
        {
            colId: 'internalTrackingNumber',
            field: 'internalTrackingNumber',
            headerName: 'Internal Tracking Number',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Internal<br />Tracking Number</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let d: IStrongBoxOpoOrderListing = params.data;
                let str = d.internalTrackingNumber;

                if (str && str.length > 14) {
                    str = `${str.slice(0, 4)}...${str.slice(-4)}`;
                }

                return <span title={d.internalTrackingNumber}>{str}</span>
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxOpoOrderListing = params.data;
                return d.internalTrackingNumber;
            },
        },
        {
            colId: 'vendorOrderId',
            field: 'vendorOrderId',
            headerName: 'Vendor Order Id',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Vendor<br />Order ID</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxOpoOrderListing = params.data;
                var str = d.vendorOrderId;
                if (str && str.length > 14) {
                    str = `${str.slice(0, 4)}...${str.slice(-4)}`;
                }
                return <span title={d.vendorOrderId}>{str}</span>
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxOpoOrderListing = params.data;
                return d.vendorOrderId;
            },
        },
        {
            colId: 'quantity',
            field: 'quantity',
            headerName: 'Quantity',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Quantity</SortableHeaderComponent>,
        },
        {
            colId: 'statusDescription',
            field: 'statusDescription',
            headerName: 'Status',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
        },
        {
            colId: 'vendorStatus',
            field: 'vendorStatus',
            headerName: 'Vendor Status',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Vendor Status</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                return <>{params.data.vendorStatus || 'N/A'}</>;
            },
            valueGetter: (params: ValueGetterParams) => params.data.vendorStatus || 'N/A',
        },
        {
            colId: 'statusDate',
            field: 'statusDate',
            headerName: 'Status Date',
            width: 120,
            comparator: Utility.dateComparator,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status Date</SortableHeaderComponent>,
        },
        {
            colId: 'notes',
            field: 'notes',
            headerName: 'Notes',
            width: 150,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Notes</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxOpoOrderListing = params.data;
                if (!d.notes) return <>N/A</>;
                const display = d.notes.length > 25 ? `${d.notes.slice(0, 25)}...` : d.notes;
                return <span title={d.notes}>{display}</span>
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxOpoOrderListing = params.data;
                return d.notes || 'N/A';
            },
        },
        {
            colId: 'compositeSwapNumber',
            field: 'compositeSwapNumber',
            headerName: 'Swap #',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Swap</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxOpoOrderListing = params.data;
                return <>{d.swapInfo?.compositeSwapNumber || 'N/A'}</>;
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxOpoOrderListing = params.data;
                return d.swapInfo?.compositeSwapNumber || 'N/A';
            },
        },
        {
            colId: 'donorCenter',
            field: 'donorCenter',
            headerName: 'Donor Center',
            width: 155,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxOpoOrderListing = params.data;
                return <DonorCenterCell donorCenter={d.swapInfo?.donorCenter} remoteDonorCenter={d.swapInfo?.remoteDonorCenter} />;
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxOpoOrderListing = params.data;
                return d.swapInfo?.donorCenter || 'N/A';
            },
        },
        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerName: 'Recipient Center',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxOpoOrderListing = params.data;
                return <>{d.swapInfo?.recipientCenter || 'N/A'}</>;
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxOpoOrderListing = params.data;
                return d.swapInfo?.recipientCenter || 'N/A';
            },
        },
        {
            colId: 'kidneyPickupDate',
            field: 'swapInfo.kidneyPickupDate',
            headerName: 'Pickup Date',
            width: 120,
            comparator: Utility.dateComparator,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Pickup Date</SortableHeaderComponent>,
        },
    ];

    const columnDefs = useMemo(() => colDefs, []);
    const rowData = useMemo(() => list, [list]);

    const apiRef = useRef<GridApi | null>(null);

    const onGridReady = (params: GridReadyEvent) => {
        apiRef.current = params.api;
        params.api.sizeColumnsToFit();
    };

    const handleExpCsv = () => {
        if (apiRef.current) {
            apiRef.current.exportDataAsCsv({
                columnGroups: false,
                fileName: `StrongBoxOpoOrderHistory.csv`,
                columnKeys: [
                    'opoName', 'createdDate', 'internalTrackingNumber', 'vendorOrderId',
                    'quantity', 'status', 'vendorStatus', 'statusDate', 'notes',
                    'compositeSwapNumber', 'donorCenter', 'recipeintCenter', 'kidneyPickupDate'
                ],
            });
        }
    };

    const getRowStyle = (params: RowClassParams) => {
        const d: IStrongBoxOpoOrderListing = params.data;
        if (d?.swapInfo?.isSwapCancelled) {
            return { background: '#D79E9B' };
        }
    }

    const legendEntries: TLegendEntry[] = [
        { text: 'Cancelled Swap', color: '#D79E9B' }
    ];

    return (
        <>
            <Row>
                <Col sm={12}>
                    <StrongBoxFilter
                        filter={filter}
                        defaultFilter={defaultFilter}
                        startDateLabel='Earliest Order Date'
                        endDateLabel='Latest Order Date'
                        legendEntries={legendEntries}
                        isCenter={false}
                        onChange={setFilter}
                        onExport={handleExpCsv}
                    />
                </Col>
            </Row>

            {loading && <LoadingIndicator type='spin' color='black' loading={true} message={'Loading Orders ...'} />}

            {error && <Alert bsStyle='danger'>{JSON.stringify(error)}</Alert>}

            {!loading &&
                <Row>
                    <Col sm={12}>
                        <GridWrapper className='ag-theme-bootstrap'>
                            <AgGridReact
                                defaultColDef={{
                                    headerClass: 'list-header',
                                    cellClass: 'list-cell',
                                    sortable: true
                                }}
                                columnDefs={columnDefs}
                                rowData={rowData}
                                headerHeight={45}
                                rowHeight={32}
                                noRowsOverlayComponentFramework={() => <div>No OPO order history records were found...</div>}
                                suppressRowClickSelection={true}
                                suppressCellSelection={true}
                                enableCellTextSelection={true}
                                groupDefaultExpanded={1}
                                groupSelectsChildren={true}
                                onGridReady={onGridReady}
                                onFirstDataRendered={(e) => e.api.sizeColumnsToFit()}
                                onGridSizeChanged={(e) => e.api.sizeColumnsToFit()}
                                onRowDataChanged={(e) => e.api.sizeColumnsToFit()}
                                getRowStyle={getRowStyle}
                                groupRowRendererParams={{
                                    suppressCount: true,
                                }}
                            />
                        </GridWrapper>
                    </Col>
                </Row>
            }
        </>
    );
}

export default StrongBoxOpoOrders
